<script setup lang="ts">
import { reset } from '@formkit/core'

const { t } = useI18n()
const apiClient = useAPIClient()
const flash = useFlash()

interface SponsorshipFields {
  name: string
  email: string
  link?: string
  message: string
}

async function submitSponsorship(fields: SponsorshipFields) {
  const { name, email, link, message } = fields
  
  const body = {
    kind: 'sponsorship', email,
    data: { name, link, message }
  }

  try {
    await apiClient.fetch('mail', { method: 'POST', body })
    reset('sponsor-form')
    flash.set(
      t('affiliates.sponsorship_form.submit_success'),
      { kind: 'success', immediate: true }
    )
  } catch (e) {
    flash.set(
      'An error has occurred', { kind: 'error', immediate: true }
    )
  }
}
</script>

<template>
  <div class="affiliates page">
    <h1>{{ $t('affiliates.title') }}</h1>

    <div class="referral">
      <h2>{{ $t('affiliates.referral_program') }}</h2>

      <i18n-t keypath="affiliates.refer_friend_desc_1">
        <template v-slot:freeMonth>
          <strong>{{ $t('affiliates.refer_friend_free_month') }}</strong>
        </template>
      </i18n-t>
      <p>{{ $t('affiliates.refer_friend_desc_2') }}</p>

      <h3>{{ $t('affiliates.refer_by_link') }}</h3>

      <p>{{ $t('affiliates.refer_by_link_desc_1') }}</p>
      <p>
        <i18n-t keypath="affiliates.refer_by_link_desc_2">
          <template v-slot:signIn>
            <NuxtLinkLocale href="/users">
              {{ $t('users.sign_in') }}
            </NuxtLinkLocale>
          </template>
        </i18n-t>
      </p>

      <h3>{{ $t('affiliates.refer_by_username') }}</h3>

      <p>{{ $t('affiliates.refer_by_username_desc') }}</p>

      <small>
        <strong>{{ $t('common.conditions') }}</strong>
        <ul>
          <li>{{ $t('affiliates.refer_conditions_1') }}</li>
          <li>{{ $t('affiliates.refer_conditions_2') }}</li>
          <li>{{ $t('affiliates.refer_conditions_3') }}</li>
        </ul>
      </small>
    </div>

    <hr>

    <div class="sponsorships">
      <h2>{{ $t('affiliates.sponsorships') }}</h2>
      <p>{{ $t('affiliates.sponsorship_desc') }}</p>

      <div class="sponsor-form">
        <FlashMessage />

        <FormKit
          type="form"
          id="sponsor-form"
          @submit="submitSponsorship"
          :submit-label="t('common.submit')"
          :submit-attrs="{ inputClass: 'button' }"
        >
          <FormKit
            type="text"
            name="name"
            :label="$t('affiliates.sponsorship_form.name')"
            validation="required"
          />
          <FormKit
            type="email"
            name="email"
            :label="$t('affiliates.sponsorship_form.email')"
            validation="required|email"
          />
          <FormKit
            type="text"
            name="link"
            :label="$t('affiliates.sponsorship_form.link')"
          />
          <FormKit
            type="textarea"
            name="message"
            :label="$t('affiliates.sponsorship_form.message')"
            validation="required"
          />
          <Captcha
            :show="apiClient.showCaptcha.value"
            @verified="p => apiClient.captchaPayload.value = p"
          />
        </FormKit>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.referral {
  margin-bottom: 1.5rem;
}
.sponsor-form {
  width: 50%;

  :deep(textarea) { width: 100%; }
}
</style>
